import React from 'react';
import _ from 'lodash';

import {
  E1ColumnData,
  LeaseApplicationStep,
  OrderStatusName,
  QueryOrdersFilterInput,
  ReconciliationReportActivityDocument,
  ReconciliationReportActivityQueryVariables,
  SalesChannelName,
  useCloseOrderMutation,
  useExportOrdersLazyQuery,
  useMockFulfillOrderMutation,
  useReconciliationReportActivityLazyQuery,
  useReconciliationReportActivity_FiltersQuery,
  useWriteOrderToE1Mutation
} from 'generated/graphql';
import {
  errorUtil,
  listQueryUtil,
  protectedFileUtil
} from 'iron-forge-react-modules';

import { DateTime, Duration } from 'luxon';
import * as types from './types';
import Big from 'big.js';



const useListQuery = listQueryUtil.genUseListQuery<types.IColumnId>();

export interface IOrderException {
  orderId: string;
  storeId: string;
  orderNumber: string;
  orderDate: DateTime;

  age: Duration;
  cacheLastUpdatedTimestamp: DateTime | null;

  canBeClosed: boolean;
  lineItemCountE1Match: boolean;
  lineItemCountShipStationMatchesSource: boolean;

  orderTotalShipStationMatchesSource: boolean;
  orderTotalE1Match: boolean;

  statusName: OrderStatusName;
  in47File: boolean;
  in42File: boolean;
  inDeviceLedger: boolean;
  inShipStation: boolean;
  hasShipped: boolean;

  paymentMethodDisplayName: string;
  smartPayLeaseApplicationNumber: string;
  lastCompletedLeaseApplicationStepDisplayName: string;

  externalId: string;
  externalUrl: string;
}

export interface IStore {
  id: string;
  name: string;
}

export interface IOrderStatus {
  name: OrderStatusName;
  displayName: string;
}

export interface IData {
  orders: IOrderException[];
}

export interface IFilterData {
  statuses: IOrderStatus[];
}


export function useFilterQuery() {
  const queryTuple = useReconciliationReportActivity_FiltersQuery();

  const wrappedTuple = React.useMemo(() => ({
    ...queryTuple,
    data: {
      statuses: _.map(queryTuple.data?.orderStatuses ?? [], (status): IOrderStatus => ({
        name: status.name,
        displayName: status.displayName
      }))
    }
  }), [queryTuple]);

  return wrappedTuple;
}



export function useQuery(salesChannel: SalesChannelName) {
  const useWrappedQuery = __genWrapQuery(salesChannel);

  const {
    fnQuery,
    queryTuple,
    fnQueryPage,
    pageInfo,
    filterState,
    gqlFilter,
    gqlSort
  } = useListQuery({
    useLazyQuery: useWrappedQuery,

    genFilter2GqlFilter: () => __filter2GqlFilter,
    defaultFilterArgs: {},
    clientColumn2GqlSortField: {
      orderNumber: 'orderNumber',
      orderDate: 'orderDate'
    },
    initQuerySort: {
      columnId: 'orderDate',
      direction: 'descending'
    },
    gqlResultData2GqlPageInfo: gqlResultData =>
      gqlResultData?.orders.pageInfo,

    gqlResultData2TupleData: (gqlTuple): IData => {
      return {
        orders: _.map(gqlTuple.data?.orders.nodes, (node): IOrderException => {
          const file42Total = __sumValuesForColumn(node.e1Data42File?.detailRows ?? [], 'SDUPRC');
          const file47Total = __sumValuesForColumn(node.e1Data?.detailRows ?? [], 'SZUPRC');

          return ({
            canBeClosed: node.status.name === 'onHold' || node.status.name === 'pending' || node.status.name === 'processing',
            orderId: node.id,
            storeId: node.store.id,
            orderNumber: node.orderNumber,

            orderDate: DateTime.fromISO(node.orderDate),
            age: DateTime.now().diff(DateTime.fromISO(node.metaCreated)),
            cacheLastUpdatedTimestamp: node.cacheLastUpdatedTimestamp ? DateTime.fromISO(node.cacheLastUpdatedTimestamp) : null,

            statusName: node.status.name,

            lineItemCountE1Match: !_.isNil(node.e1Data) && !_.isNil(node.e1Data42File) && node.e1Data?.detailRows.length === node.e1Data42File?.detailRows.length,
            lineItemCountShipStationMatchesSource: node.lineItems.length === node.shipStationOrder?.rawData.items?.length,

            // Hack
            orderTotalShipStationMatchesSource: new Big(node.totalCents).eq(new Big(node.shipStationOrder?.rawData.orderTotal ?? 0).mul(100)),
            orderTotalE1Match: file42Total === file47Total,
            inDeviceLedger: !_.isNil(node.e1DeviceLedger) && !_.isEmpty(node.e1DeviceLedger),
            in47File: !_.isNil(node.e1Data),
            in42File: !_.isNil(node.e1Data42File),
            inShipStation: !_.isNil(node.shipStationOrder),
            hasShipped: !_.isNil(node.shipment),

            paymentMethodDisplayName: node.paymentMethod.displayName,
            smartPayLeaseApplicationNumber: node.smartPayLeaseApplication?.applicationNumber ? `#${node.smartPayLeaseApplication.applicationNumber}` : '',
            lastCompletedLeaseApplicationStepDisplayName: __getLastCompletedStep(node.smartPayLeaseApplication?.steps ?? [])?.name.displayName ?? '',

            externalId: node.rawSourceOrder?.externalId ?? '',
            externalUrl: node.rawSourceOrder?.url ?? ''
          });
        })
      };
    }
  });



  const [fnExport, exportQueryTuple] = useExportOrdersToCsv(filterState);



  return {
    fnQuery,
    queryTuple: {
      ...queryTuple,
      loading: queryTuple.loading || exportQueryTuple.loading
    },
    fnQueryPage,
    pageInfo,
    filterState,
    fnExport,
    gqlFilter,
    gqlSort
  };
}



function __genWrapQuery(salesChannel: SalesChannelName) {
  function useWrappedQuery() {
    const [fnQuery, tuple] = errorUtil.useWrapLazyQueryOrMutationTuple(
      useReconciliationReportActivityLazyQuery({
        fetchPolicy: 'no-cache',
        notifyOnNetworkStatusChange: true
      }));

    const fnWrappedQuery = React.useCallback(
      (params: {
        variables: Pick<ReconciliationReportActivityQueryVariables, 'page' | 'filter'>;
      }) => {
        fnQuery({
          variables: {
            page: params.variables.page,
            filter: {
              ...params.variables.filter,
              salesChannel
            }
          }
        });
      },
      [fnQuery]
    );

    return [
      fnWrappedQuery,
      tuple
    ] as const;
  }

  return useWrappedQuery;
}



export function useWriteOrderToE1() {
  const [fnMutate, tuple] = useWriteOrderToE1Mutation({});

  const fnWrappedMutate = React.useCallback((orderId: string) => {
    fnMutate({
      variables: {
        orderId
      }
    });
  }, [fnMutate]);

  return [
    fnWrappedMutate,
    {
      ...tuple,
      data: null
    }
  ] as const;
}



function useExportOrdersToCsv(filterState?: types.IFilter) {
  const [fnQuery, tuple] = useExportOrdersLazyQuery({ fetchPolicy: 'no-cache' });

  const fnWrappedQuery = React.useCallback(
    () => {
      fnQuery({
        variables: {
          options: {
            filter: __filter2GqlFilter(filterState)
          }
        }
      });
    },
    [fnQuery, filterState]
  );

  React.useEffect(
    () => {
      if (tuple.called && !tuple.loading && tuple.data) {
        protectedFileUtil.forceDownloadEncodedFile(tuple.data.ordersAsCsv);
      }
    },
    [tuple]
  );

  return [
    fnWrappedQuery,
    tuple
  ] as const;
}



function __filter2GqlFilter(filter?: types.IFilter): QueryOrdersFilterInput {
  return {
    omniFreeText: filter?.freeText ?? undefined,
    storeId: filter?.storeId || undefined,
    withStatus: filter?.statuses ?? ['completed', 'onHold', 'pending', 'processing'],
    after: filter?.after.toISO() ?? DateTime.now().minus({ days: 3 }).startOf('day').toISO(),
    before: filter?.before.toISO() ?? DateTime.now().endOf('day').toISO(),
    withPaymentMethod: filter?.paymentMethodName,
    withLeaseApplicationStepCompleted: filter?.leaseApplicationStepName ? [filter?.leaseApplicationStepName] : undefined
  };
}



function __sumValuesForColumn(data: E1ColumnData[][], columnName: string) {
  return _.sumBy(data, row => _.toNumber(_.find(row, r => r.columnName === columnName)?.value ?? '0'));
}



function __getLastCompletedStep(steps: LeaseApplicationStep[]) {
  const completedSteps = _.filter(steps, step => step.status.name === 'complete');
  return _.isEmpty(completedSteps) ? null : _.last(completedSteps);
}



export function useCloseOrder() {
  const [fnMutate, tuple] = useCloseOrderMutation({
    refetchQueries: [
      {
        query: ReconciliationReportActivityDocument,
        variables: {}
      }
    ]
  });

  const fnWrappedMutate = React.useCallback(async (orderId: string) => {
    await fnMutate({
      variables: {
        orderId
      }
    });
  }, [fnMutate]);

  return React.useMemo(() => [
    fnWrappedMutate,
    {
      ...tuple,
      data: null
    }
  ] as const, [
    fnWrappedMutate,
    tuple
  ]);
}



export function useMockFulfillOrder() {
  const [fnMutate, tuple] = useMockFulfillOrderMutation({
    refetchQueries: []
  });

  const fnWrappedMutate = React.useCallback(
    (orderId: string) => {
      fnMutate({
        variables: {
          orderId
        }
      });
    },
    [fnMutate]
  );

  return React.useMemo(
    () => [
      fnWrappedMutate,
      {
        ...tuple,
        data: null
      }
    ] as const,
    [
      fnWrappedMutate,
      tuple
    ]
  );
}
